import React, { FC, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  TextField,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";
import { createSite, getRegion, updateSite } from "api";
import { useAmplitude, useNotify } from "hooks";
import { QUERY_KEYS } from "consts";
import { COLORS, InputFieldWrapper, TYPOGRAPHY_COMMON_STYLES } from "components";
import { formatDateToISOStartOfDay, getDateFormatset } from "utils";
import { AddSiteModalProps } from "./types";

export const AddSiteModal: FC<AddSiteModalProps> = ({
  isOpen,
  onClose,
  allowedValues,
  siteData,
  clickType = "",
  editNotes = false,
  openFromActiveTab,
}) => {
  const { countries, siteStatuses, siteTypes, countryRegions, siteAreaUnits } = allowedValues;
  const sortedCountries = countryRegions.sort((a, b) => a.localeCompare(b));
  const CountriesAccending = countries?.sort() || [];
  const siteTypesAccending = siteTypes?.sort() || [];

  const notify = useNotify();
  const queryClient = useQueryClient();
  const { trackEvent } = useAmplitude();

  const [formValues, setFormValues] = useState<any>({ regionOptions: [] });
  const [loadingRegionCode, setLoadingRegionCode] = useState<boolean>(false);

  const defaultValues: any = useMemo(() => {
    const endDate = clickType === "Enable"
      ? "2999-12-31T00:00:00Z"
      : clickType === ""
        ? siteData?.endDate || null
        : formatDateToISOStartOfDay(new Date());

    return {
      name: siteData?.name || null,
      address: siteData?.address || null,
      city: siteData?.city || null,
      zipCode: siteData?.zipCode || null,
      type: siteData?.type || null,
      status: siteData?.status || null,
      country: siteData?.country || null,
      countryRegion: siteData?.countryRegion || null,
      region: siteData?.region || null,
      customerSiteId: siteData?.customerSiteId || null,
      businessUnit: siteData?.businessUnit || null,
      startDate: siteData?.startDate || null,
      endDate,
      siteArea: siteData?.siteArea || null,
      siteAreaUnit: siteData?.siteAreaUnit || null,
      notes: siteData?.notes
    };
  }, [siteData, clickType]);

  const { control, reset, getValues, watch, setValue } = useForm<any>({
    mode: "onChange",
    defaultValues,
  });

  const [watchedCountryValue, watchedZipValue] = watch(['country', 'zipCode']);

  const { mutateAsync: createSiteRequest, isLoading } = useMutation(createSite);
  const { mutateAsync: updateSiteRequest, isLoading: updateLoading } = useMutation(updateSite);


  async function fetchData(dataset: any) {
    setLoadingRegionCode(true);
    try {
      const regionOptionsResponse = await getRegion(dataset);
      // When the site has customRegion, we only show it in the UI and never persist it to the form's region field.
      // So we skip updating form region here to avoid overwriting the stored value on country/zip change.
      if (!siteData?.customRegion) {
        const currentRegion = getValues('region');
        // If there is only one region option and it doesn't match the current region, set the region to the first option
        if (regionOptionsResponse.length === 1 && regionOptionsResponse[0] !== currentRegion) {
          setValue('region', regionOptionsResponse[0]);
        } else if (regionOptionsResponse.length > 1 && !regionOptionsResponse.includes(currentRegion)) {
          // If there are multiple region options and the current region is not in the list, set the region to null
          setValue('region', null);
        }
      }

      setFormValues((prevValues: any) => ({
        ...prevValues,
        regionOptions: regionOptionsResponse,
      }));

    } catch (error) {
      console.error('Failed to fetch region data:', error);
      notify.error('Failed to fetch region data');
    } finally {
      setLoadingRegionCode(false);
    }
  }

  const handleAddSite = async () => {
    if (openFromActiveTab) {
      trackEvent("Added Site (Manual)", "Site Management - Active");
    };
    await createSiteRequest(getValues(), {
      onError: () => notify.error(`Failed to create site: ${getValues().name}!`),
      onSuccess: () => {
        notify.success(`Site successfully created: ${getValues().name}!`);
        queryClient.invalidateQueries([QUERY_KEYS.SITES]);
      },
    });
    reset(defaultValues);
    onClose();
  };

  const handleEditSite = async () => {
    const { id } = siteData;
    if (formValues.region === "MULTIPLE REGIONS FOUND") {
      notify.error("Please make sure to select an Electricity Region Code to calculate your emissions.");
    } else {
      const currentValues = getValues();

      const updatedSite = {
        ...formValues,
        id,
        name: currentValues.name === "" ? null : currentValues.name,
        address: currentValues.address === "" ? null : currentValues.address,
        city: currentValues.city === "" ? null : currentValues.city,
        zipCode: currentValues.zipCode === "" ? null : currentValues.zipCode,
        country: currentValues.country === "" ? null : currentValues.country,
        type: currentValues.type === "" ? null : currentValues.type,
        status: currentValues.status === "" ? null : currentValues.status,
        countryRegion: currentValues.countryRegion === "" ? null : currentValues.countryRegion,
        region: currentValues.region === "" ? null : currentValues.region,
        customerSiteId: currentValues?.customerSiteId === "" ? null : currentValues?.customerSiteId,
        businessUnit: currentValues?.businessUnit === "" ? null : currentValues?.businessUnit,
        startDate: currentValues?.startDate === "" ? null : currentValues?.startDate,
        endDate: currentValues?.endDate === "" ? null : currentValues?.endDate,
        siteArea: currentValues?.siteArea === "" ? null : currentValues?.siteArea,
        siteAreaUnit: currentValues?.siteAreaUnit === "" ? null : currentValues?.siteAreaUnit,
        notes: currentValues?.notes === "" ? null : currentValues?.notes
      };

      await updateSiteRequest(updatedSite, {
        onError: () => notify.error(`Failed to edit site: ${formValues.name}!`),
        onSuccess: () => {
          notify.success(`Site successfully edited: ${formValues.name}!`);
          queryClient.invalidateQueries([QUERY_KEYS.SITES]);
        },
      });
      setFormValues({});
      onClose();
    }
  };

  const handleCancel = () => {
    if (siteData) {
      onClose();
    } else {
      onClose();
      setFormValues({});
      reset(defaultValues);
    }
  }

  useEffect(() => {
    reset(defaultValues);
    const initialRegionOptions = siteData?.region ? [siteData.region] : [];
    setFormValues({ ...(siteData || {}), regionOptions: initialRegionOptions });
  }, [reset, defaultValues, siteData, setFormValues]);

  useEffect(() => {
    if (watchedCountryValue) {
      fetchData({
        zipCode: watchedZipValue ?? null,
        country: watchedCountryValue
      });
    }
  }, [watchedCountryValue, watchedZipValue]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>{editNotes ? "Site Notes" : siteData ? "Edit Site" : "Add New Site"}</DialogTitle>
      <DialogContent>
        <form onSubmit={(e) => e.preventDefault()}>
          <Stack
            sx={{
              width: "100%",
              minWidth: { xs: "300px", sm: "360px", md: "400px" },
              gap: "1.5rem",
              marginTop: "7px",
            }}
          >
            {editNotes
              ? <Grid item xs={6}>
                <InputFieldWrapper label="Notes">
                  <Controller
                    name="notes"
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextField
                        sx={{ width: "100%" }}
                        helperText={error ? error.message : null}
                        size="small"
                        error={!!error}
                        onChange={onChange}
                        value={value}
                        multiline
                        rows={3}
                      />
                    )}
                  />
                </InputFieldWrapper>
              </Grid>
              : <Grid container spacing={2}>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Site Name">
                    <Controller
                      name="name"
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Customer Site ID">
                    <Controller
                      name="customerSiteId"
                      control={control}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Business Unit">
                    <Controller
                      name="businessUnit"
                      control={control}
                      render={({
                        fieldState: { error },
                        field: { onChange, value },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Street Address">
                    <Controller
                      name="address"
                      control={control}
                      render={({
                        fieldState: { error },
                        field: { onChange, value },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                      >
                        <InputFieldWrapper label="Start Date">
                          <DatePicker
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: {
                                  width: "100%",
                                }
                              },
                            }}
                            value={
                              field?.value
                                ? dayjs(field?.value).utc()
                                : null
                            }
                            onChange={(date) => field.onChange(dayjs(date).format('YYYY-MM-DDTHH:mm:ss[Z]'))}
                            format={getDateFormatset()}
                          />
                        </InputFieldWrapper>
                      </LocalizationProvider>
                    )
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                      >
                        <InputFieldWrapper label="End Date">
                          <DatePicker
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: {
                                  width: "100%",
                                  "& fieldset": {
                                    border: clickType === "Disable" ? "1.2px solid red" : "",
                                  },
                                  "& .MuiOutlinedInput-root": {
                                    "&:hover fieldset": {
                                      borderColor: clickType === "Disable" ? "red" : "", // remove hover effect if needed
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: clickType === "Disable" ? "red" : "", // remove focus effect if needed
                                    },
                                  },
                                }
                              },
                            }}
                            value={
                              field?.value
                                ? dayjs(field?.value).utc()
                                : null
                            }
                            onChange={(date) => field.onChange(dayjs(date).format('YYYY-MM-DDTHH:mm:ss[Z]'))}
                            format={getDateFormatset()}
                          />
                        </InputFieldWrapper>
                      </LocalizationProvider>
                    )
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="City">
                    <Controller
                      name="city"
                      control={control}
                      render={({
                        fieldState: { error },
                        field: { onChange, value },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Zipcode">
                    <Controller
                      name="zipCode"
                      control={control}
                      render={({
                        fieldState: { error },
                        field: { onChange, value },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Type">
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="type-simple-select-label"
                          id="type-simple-select"
                          autoWidth
                          fullWidth
                          sx={{ height: 40 }}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 260,
                                maxWidth: 'auto',
                                whiteSpace: 'normal',

                              },
                            },
                          }}
                        >
                          {siteTypesAccending.map((t) => (
                            <MenuItem
                              sx={{
                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                fontSize: 14,
                                lineHeight: "18px",
                                color: '#90959E',
                                '&:hover': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                              }} key={t} value={t}>
                              {t}</MenuItem>
                          ))}
                        </Select>
                      )
                      }
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Status">
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="status-simple-select-label"
                          id="status-simple-select"
                          autoWidth
                          fullWidth
                          sx={{ height: 40 }}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 260,
                                maxWidth: 'auto',
                                whiteSpace: 'normal',

                              },
                            },
                          }}
                        >
                          {siteStatuses.map((t) => (
                            <MenuItem
                              sx={{
                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                fontSize: 14,
                                lineHeight: "18px",
                                color: '#90959E',
                                '&:hover': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                              }} key={t} value={t}>
                              {t}</MenuItem>
                          ))}
                        </Select>
                      )
                      }
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Country">
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="country-simple-select-label"
                          id="country-simple-select"
                          autoWidth
                          fullWidth
                          sx={{ height: 40 }}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 260,
                                maxWidth: 'auto',
                                whiteSpace: 'normal',

                              },
                            },
                          }}
                        >
                          {CountriesAccending.map((t) => (
                            <MenuItem
                              sx={{
                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                fontSize: 14,
                                lineHeight: "18px",
                                color: '#90959E',
                                '&:hover': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                              }} key={t} value={t}>
                              {t}</MenuItem>
                          ))}
                        </Select>
                      )
                      }
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Region">
                    <Controller
                      name="countryRegion"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="countryRegion-simple-select-label"
                          id="countryRegion-simple-select"
                          autoWidth
                          fullWidth
                          sx={{ height: 40 }}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 260,
                                maxWidth: 'auto',
                                whiteSpace: 'normal',

                              },
                            },
                          }}
                        >
                          {sortedCountries.map((country) => (
                            <MenuItem
                              sx={{
                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                fontSize: 14,
                                lineHeight: "18px",
                                color: '#90959E',
                                '&:hover': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                              }} key={country} value={country}>
                              {country.replaceAll("_", " ")}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Electricity Region Code">
                    {/* Electricity Region Code: when siteData.customRegion is set, we show it read-only in this field
                        and do not store it in form values. On save, region stays as siteData.region (or null). */}
                    <Controller
                      name="region"
                      control={control}
                      render={({ field }) => {
                        const hasCustomRegion = !!siteData?.customRegion;
                        const regionOptions = hasCustomRegion
                          ? [siteData.customRegion]
                          : (formValues?.regionOptions || []);
                        const displayValue = hasCustomRegion ? siteData.customRegion : (field.value ?? '');
                        return (
                          <Select
                            labelId="region-simple-select-label"
                            id="region-simple-select"
                            name="region"
                            aria-label="Electricity Region Code"
                            data-testid="region-select"
                            autoWidth
                            fullWidth
                            sx={{ height: 40, color: (formValues?.regionOptions?.length > 1) && !hasCustomRegion ? 'red' : 'inherit', }}
                            value={displayValue}
                            onChange={(e) => { if (!hasCustomRegion) field.onChange(e.target.value); }}
                            disabled={hasCustomRegion || (formValues?.regionOptions?.length || 0) <= 1}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  minWidth: 260,
                                  maxWidth: 'auto',
                                  whiteSpace: 'normal',
                                },
                              },
                            }}
                          >
                            {regionOptions.map((option: string) => (
                              <MenuItem
                                sx={{
                                  fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                  fontSize: 14,
                                  lineHeight: "18px",
                                  color: '#90959E',
                                  '&:hover': {
                                    backgroundColor: '#F6F8FA',
                                    borderRadius: 5,
                                    color: COLORS.Romance,
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: '#F6F8FA',
                                    borderRadius: 5,
                                    color: COLORS.Romance,
                                  },
                                }}
                                key={option}
                                value={option}
                              >
                                {loadingRegionCode
                                  ? <CircularProgress sx={{ color: COLORS.Romance, font: '18px', mx: '4px' }} size={16} />
                                  : option
                                }
                              </MenuItem>
                            ))}
                          </Select>
                        );
                      }}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Site Area">
                    <Controller
                      name="siteArea"
                      control={control}
                      render={({
                        fieldState: { error },
                        field: { onChange, value },
                      }) => (
                        <TextField
                          helperText={error ? error.message : null}
                          size="small"
                          error={!!error}
                          onChange={onChange}
                          value={value}
                          fullWidth
                          variant="outlined"
                        />
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <InputFieldWrapper label="Site Area Unit">
                    <Controller
                      name="siteAreaUnit"
                      control={control}
                      render={({ field }) => (
                        <Select
                          labelId="siteAreaUnit-simple-select-label"
                          id="siteAreaUnit-simple-select"
                          autoWidth
                          fullWidth
                          sx={{ height: 40 }}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                minWidth: 260,
                                maxWidth: 'auto',
                                whiteSpace: 'normal',

                              },
                            },
                          }}
                        >
                          {siteAreaUnits?.map((unit) => (
                            <MenuItem
                              sx={{
                                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                                fontSize: 14,
                                lineHeight: "18px",
                                color: '#90959E',
                                '&:hover': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#F6F8FA',
                                  borderRadius: 5,
                                  color: COLORS.Romance,
                                },
                              }} key={unit} value={unit}>
                              {unit}</MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </InputFieldWrapper>
                </Grid>
              </Grid>
            }
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          onClick={siteData ? handleEditSite : handleAddSite}
          variant="contained"
          color="primary"
          disabled={updateLoading || isLoading}
        >
          {updateLoading && <CircularProgress sx={{ color: COLORS.Romance, font: '18px', mx: '4px', position: 'absolute' }} size={20} />}
          {siteData ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
